import { useEffect, useState } from "react";
import { apiGetSettings } from "../../graphQL/settings";
import { useSettingsDomain, settingsScope } from "../../graphQL/settings/scope";

const normalizeEntries = (payload) => {
  const raw = Array.isArray(payload) ? payload : Array.isArray(payload?.data) ? payload.data : [];
  const seen = new Set();
  const out = [];
  for (const entry of raw) {
    const value = String(
      typeof entry === "string"
        ? entry
        : entry?.value || entry?.name || entry?.label || entry?.id || ""
    ).trim();
    if (!value) continue;
    if (entry && typeof entry === "object" && entry.isActive === false) continue;
    const key = value.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(value);
  }
  return out.sort((a, b) => a.localeCompare(b));
};

// Scope comes from the active domain; the legacy authorId argument is ignored.
export const usePropertyStateList = () => {
  const domain = useSettingsDomain();
  const [states, setStates] = useState([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const queries = [
          { schemaType: "state_list", type: "property", ...settingsScope(domain) },
          { schemaType: "state_list", type: "property" },
          { schemaType: "system", type: "property", ...settingsScope(domain) },
          { schemaType: "system", type: "property" },
        ];
        const responses = await Promise.all(queries.map((input) => apiGetSettings(input)));
        const allSettings = responses.flatMap((res) =>
          Array.isArray(res?.settings) ? res.settings : []
        );

        const KNOWN_STATES = [
          "gis_ingested",
          "gis_not_found",
          "gis_ambiguous",
          "review",
          "verify",
        ];
        const merged = normalizeEntries(allSettings.flatMap((s) => normalizeEntries(s?.data)));
        // Always union known states with whatever the settings API returned so new
        // quality flags appear even if the settings table hasn't been updated yet.
        const finalValues = normalizeEntries([...(merged || []), ...KNOWN_STATES]);
        if (cancelled) return;
        setStates(finalValues);
      } catch (e) {
        console.error("Failed to load property state list:", e);
        if (!cancelled) setStates([
          "gis_ingested",
          "gis_not_found",
          "gis_ambiguous",
        ]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [domain]);

  return states;
};
